import debounce from "debounce";
import { LoaderCircle, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Bank } from "@/apis/gql/types";
import { DialogTitle } from "@/components/ui/dialog";
import { DialogDescription } from "@/components/ui/dialog";
import { DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useGetAllBanks } from "@/queries/banks/get-all";
import { motion } from "framer-motion";
import BankCard, { BankCardSkeleton } from "./bank-card";

type Props = {
  onSelect: (bank: Bank) => void;
  loading?: boolean;
};

export const BankSelectionStep = ({ onSelect, loading }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { banks, loading: banksLoading } = useGetAllBanks({
    nameSubstring: search,
  });

  const debouncedSearch = debounce((value: string) => {
    setSearch(value);
  }, 300);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleBankSelect = useCallback(
    (bank: Bank) => {
      onSelect(bank);
    },
    [onSelect],
  );

  return (
    <div className="flex flex-col h-[60vh] overflow-hidden">
      <DialogHeader className="flex-shrink-0">
        <DialogTitle>
          {t("bank_connections.connection_step_zero.title")}
        </DialogTitle>
        <DialogDescription>
          {t("bank_connections.connection_step_zero.description")}
        </DialogDescription>
      </DialogHeader>
      <div className="relative w-full flex-shrink-0 my-4">
        <Input
          placeholder={t(
            "bank_connections.connection_step_zero.search_placeholder",
          )}
          onChange={handleSearch}
          className="pl-7 w-full"
        />
        <Search
          width={16}
          height={16}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 select-none opacity-50"
        />
        {banksLoading && (
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 select-none opacity-50">
            <LoaderCircle size={18} className="animate-spin" />
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        <div className="grid gap-4">
          {banksLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: not a problem here
              <BankCardSkeleton key={index} />
            ))
          ) : banks.length > 0 ? (
            banks.map((bank) => (
              <motion.div
                key={bank.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.1 }}
                whileHover={{ scale: 1.001 }}
              >
                <BankCard
                  bank={bank}
                  onSelect={() => handleBankSelect(bank)}
                  disabled={loading}
                />
              </motion.div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-8 text-gray-500">
              <p className="text-lg font-medium">
                {t("bank_connections.connection_step_zero.no_banks_found")}
              </p>
              <p className="text-sm">
                {t(
                  "bank_connections.connection_step_zero.try_different_search_term",
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
